import { motion } from 'framer-motion';
import { useState } from 'react';

export default function Pricing() {
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [additionalCans, setAdditionalCans] = useState(0);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);

  const plans = [
    {
      id: 'one-time',
      name: 'One-Time Clean',
      price: '$70',
      basePrice: 70,
      period: '/per clean',
      features: [
        'Up to 2 cans included',
        'Complete deep cleaning',
        'Inside & outside scrub',
        'Sanitize & deodorize',
        'Eco-friendly products',
        'Additional cans $10/ea',
        'Perfect for trying us out'
      ],
      color: 'blue',
      borderColor: 'border-blue-600',
      buttonColor: 'bg-blue-600 hover:bg-blue-700',
      popular: false
    },
    {
      id: 'quarterly',
      name: 'Quarterly Clean',
      price: '$35',
      basePrice: 35,
      period: '/every three months',
      features: [
        'Up to 2 cans included',
        'Everything in One-Time',
        'Service every 3 months',
        '50% savings per clean',
        'Automatic scheduling',
        'Additional cans $10/ea',
        'Cancel anytime'
      ],
      color: 'red',
      borderColor: 'border-red-500',
      buttonColor: 'bg-red-500 hover:bg-red-600',
      popular: false
    },
    {
      id: 'monthly',
      name: 'Monthly Clean',
      price: '$20',
      basePrice: 20,
      period: '/per month',
      features: [
        'Up to 2 cans included',
        'Everything in Quarterly',
        'Service every month',
        'Best value - 71% savings',
        'Always fresh & clean',
        'Additional cans $10/ea',
        'Cancel anytime'
      ],
      color: 'green',
      borderColor: 'border-green-500',
      buttonColor: 'bg-green-500 hover:bg-green-600',
      popular: true
    }
  ];

  const handleChoosePlan = (planId: string) => {
    setSelectedPlan(planId);
    setAdditionalCans(0);
  };

  const handleCheckout = async (planId: string) => {
    console.log('🚀 Starting checkout process...', {
      planId,
      additionalCans,
      supabaseUrl: import.meta.env.VITE_PUBLIC_SUPABASE_URL,
      hasAnonKey: !!import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
    });

    setIsCheckoutLoading(true);
    try {
      const requestBody = {
        planType: planId,
        additionalCans: additionalCans,
        successUrl: `${window.location.origin}/?payment=success`,
        cancelUrl: `${window.location.origin}/#pricing`,
      };

      console.log('📦 Request body:', requestBody);
      console.log('🌐 Request URL:', `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-checkout-session`);

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-checkout-session`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify(requestBody),
        }
      );

      console.log('📡 Response status:', response.status);
      console.log('📡 Response ok:', response.ok);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Response error text:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const data = await response.json();
      console.log('📦 Response data:', data);
      
      if (data.url) {
        console.log('✅ Got checkout URL, redirecting to:', data.url);
        window.location.href = data.url;
      } else if (data.error) {
        console.error('❌ API returned error:', data.error);
        throw new Error(data.error);
      } else {
        console.error('❌ No URL in response:', data);
        throw new Error('Failed to create checkout session - no URL returned');
      }
    } catch (error) {
      console.error('❌ Checkout error:', error);
      alert(`Failed to start checkout: ${error.message}. Please try again.`);
    } finally {
      setIsCheckoutLoading(false);
    }
  };

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const calculateTotal = (basePrice: number) => {
    return basePrice + (additionalCans * 10);
  };

  return (
    <section id="pricing" className="py-16 sm:py-24 bg-gradient-to-br from-green-100 via-blue-100 to-yellow-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 sm:mb-16"
        >
          <h2 className="text-5xl sm:text-6xl md:text-7xl font-black text-blue-600 mb-4 sm:mb-6" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
            Our Prices
          </h2>
          <p className="text-lg sm:text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto px-4">
            Choose the plan that works best for you!
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto">
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15, duration: 0.6 }}
              className={`relative bg-white rounded-[48px] border-8 ${plan.borderColor} p-12 shadow-xl ${
                plan.popular ? 'md:-translate-y-10' : ''
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-6 -right-6 bg-yellow-400 text-red-600 font-bold px-6 py-3 rounded-full shadow-lg transform rotate-12 text-lg">
                  Biggest Value!
                </div>
              )}

              <div className={`${plan.buttonColor.split(' ')[0]} text-white text-center py-4 rounded-full mb-8 -mx-4 -mt-4`}>
                <h3 className="text-2xl font-bold uppercase tracking-wide" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
                  {plan.name}
                </h3>
              </div>

              <div className="text-center mb-8">
                <div className="text-6xl font-black text-gray-900 mb-2" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
                  {selectedPlan === plan.id && additionalCans > 0 
                    ? `$${calculateTotal(plan.basePrice)}`
                    : plan.price
                  }
                </div>
                <div className="text-xl text-gray-500 font-semibold">{plan.period}</div>
                {selectedPlan === plan.id && additionalCans > 0 && (
                  <div className="text-sm text-gray-600 mt-2">
                    Base: ${plan.basePrice} + {additionalCans} extra can{additionalCans > 1 ? 's' : ''} (${additionalCans * 10})
                  </div>
                )}
              </div>

              <div className="border-t-4 border-dashed border-gray-300 mb-8"></div>

              <ul className="space-y-4 mb-10">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <i className={`ri-checkbox-circle-fill text-2xl flex-shrink-0 ${
                      idx % 3 === 0 ? 'text-red-500' : idx % 3 === 1 ? 'text-blue-600' : 'text-yellow-500'
                    }`}></i>
                    <span className="text-gray-700 text-lg leading-tight">{feature}</span>
                  </li>
                ))}
              </ul>

              {selectedPlan === plan.id ? (
                <div className="space-y-4">
                  <div className="bg-gray-50 p-6 rounded-3xl border-2 border-gray-200">
                    <label className="block text-center text-gray-700 font-bold mb-4 text-lg">
                      Additional Cans ($10 each)
                    </label>
                    <div className="flex items-center justify-center gap-4">
                      <button
                        onClick={() => setAdditionalCans(Math.max(0, additionalCans - 1))}
                        disabled={additionalCans === 0}
                        className={`w-12 h-12 rounded-full font-bold text-2xl flex items-center justify-center whitespace-nowrap cursor-pointer ${
                          additionalCans === 0 
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                            : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                        }`}
                      >
                        -
                      </button>
                      <div className="text-4xl font-black text-gray-900 w-16 text-center" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
                        {additionalCans}
                      </div>
                      <button
                        onClick={() => setAdditionalCans(additionalCans + 1)}
                        className="w-12 h-12 rounded-full bg-gray-300 hover:bg-gray-400 text-gray-700 font-bold text-2xl flex items-center justify-center whitespace-nowrap cursor-pointer"
                      >
                        +
                      </button>
                    </div>
                    <p className="text-center text-sm text-gray-600 mt-3">
                      Total cans: {2 + additionalCans}
                    </p>
                  </div>

                  <button
                    onClick={() => handleCheckout(plan.id)}
                    disabled={isCheckoutLoading}
                    className={`w-full ${plan.buttonColor} text-white py-5 rounded-full text-xl font-bold shadow-lg hover:scale-105 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isCheckoutLoading ? 'Loading...' : 'Proceed to Checkout'}
                  </button>

                  <button
                    onClick={() => setSelectedPlan(null)}
                    className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 py-3 rounded-full text-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
                  >
                    Back to Plans
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => handleChoosePlan(plan.id)}
                  className={`w-full ${plan.buttonColor} text-white py-5 rounded-full text-xl font-bold shadow-lg hover:scale-105 transition-all whitespace-nowrap cursor-pointer`}
                >
                  Choose Plan
                </button>
              )}
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="text-center mt-12 sm:mt-16"
        >
          <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-6 sm:mb-8 px-4">
            <strong>Need more than 2 cans?</strong> Just $10 per additional can for any plan!
          </p>
        </motion.div>
      </div>
    </section>
  );
}